import type { ReactNode } from "react";
import { cn, Icon, Input, Label, Switch, type IconName } from "@nexus/ui";

interface SettingsCardProps {
  icon: IconName;
  title: string;
  description: string;
  children: ReactNode;
}

/** One group of related settings. Four of these make up the settings tab. */
export function SettingsCard({ icon, title, description, children }: SettingsCardProps) {
  return (
    <section className="rounded-md border border-border bg-card">
      <header className="flex items-start gap-3 border-b border-border/70 px-5 py-4">
        <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-sm bg-muted">
          <Icon name={icon} size="sm" color="muted" aria-hidden />
        </span>
        <div className="min-w-0">
          <h2 className="m-0 text-sm font-semibold text-foreground">{title}</h2>
          <p className="m-0 mt-0.5 max-w-[68ch] text-xs text-muted-foreground">{description}</p>
        </div>
      </header>
      <div className="divide-y divide-border/60">{children}</div>
    </section>
  );
}

interface SettingRowProps {
  /** Points the label at the control. Omit for a control that labels itself. */
  htmlFor?: string;
  label: string;
  description?: string;
  /**
   * Draws the row as a consequence of the one above it — used where a toggle
   * decides whether the rows under it mean anything.
   */
  isNested?: boolean;
  /** Dims the row and marks the change as a no-op while it stays off. */
  isMuted?: boolean;
  control: ReactNode;
}

export function SettingRow({
  htmlFor,
  label,
  description,
  isNested = false,
  isMuted = false,
  control,
}: SettingRowProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-6 px-5 py-3.5 transition-opacity",
        isNested && "border-l-2 border-l-border/70 bg-muted/20 pl-8",
        isMuted && "opacity-55",
      )}
    >
      <div className="min-w-0 flex-1">
        <Label
          htmlFor={htmlFor}
          className="text-xs font-medium text-foreground data-[disabled]:opacity-100"
        >
          {label}
        </Label>
        {description ? (
          <p className="m-0 mt-0.5 max-w-[62ch] text-xs leading-relaxed text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center justify-end">{control}</div>
    </div>
  );
}

interface SettingSwitchProps {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}

export function SettingSwitch({ id, checked, disabled, onCheckedChange }: SettingSwitchProps) {
  return (
    <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
  );
}

interface NumberFieldProps {
  id: string;
  value: number;
  min: number;
  max: number;
  /** Rendered after the input — "minutes", "days", the unit the number is in. */
  unit?: string;
  disabled?: boolean;
  onChange: (value: number) => void;
}

/**
 * A bounded number input that keeps what is typed until focus leaves.
 *
 * Clamping on every keystroke is what makes these fields hostile: clearing the
 * box to type `120` briefly reads as empty, and a field that rewrites itself to
 * the minimum mid-word cannot be typed into at all. So the draft only takes a
 * number the range allows, and blur is where an out-of-range one is pulled back.
 */
export function NumberField({
  id,
  value,
  min,
  max,
  unit,
  disabled = false,
  onChange,
}: NumberFieldProps) {
  return (
    <div className="flex items-center gap-2">
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (event.target.value === "" || Number.isNaN(next)) return;
          onChange(next);
        }}
        onBlur={(event) => {
          const next = Number(event.target.value);
          if (Number.isNaN(next)) return onChange(min);
          onChange(Math.min(max, Math.max(min, next)));
        }}
        className="h-8 w-20 text-xs tabular-nums"
      />
      {unit ? <span className="w-16 text-xs text-muted-foreground">{unit}</span> : null}
    </div>
  );
}

/**
 * Pinned save bar for settings pages. Status and actions sit together on the
 * right so a fixed devtools chip in the bottom-left corner cannot eat the
 * credit line — the left stays intentionally empty.
 */
export function SettingsSaveFooter({
  status,
  children,
}: {
  status: ReactNode;
  children?: ReactNode;
}) {
  return (
    <footer className="shrink-0 border-t border-border bg-background px-5 py-3.5">
      <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
        <p
          className="m-0 max-w-prose text-right text-xs leading-snug text-muted-foreground"
          role="status"
          aria-live="polite"
        >
          {status}
        </p>
        {children ? (
          <div className="flex shrink-0 items-center gap-2">{children}</div>
        ) : null}
      </div>
    </footer>
  );
}
